import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import StyledLoadingSpinner from "../../components/ui/StyledLoadingSpinner";
import MetaHead from "../../components/ui/MetaHead";
import { useAuth } from "../../contexts/AuthContext";
import EditBlogModal from "../../components/Blog/EditBlogModal";
import { deleteBlog, getBlogs } from "../../services/firebase";
import BlogDetailsSideNav from "./BlogDetailsSideNav";
import { BlogType } from "../../types/blog";
import {
  formatToEasternTime,
  formatToET,
} from "../../components/utils/formatToET";
import { DeleteModal } from "../../components/DeleteModal";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { addImageClass } from "../../components/utils/resizeImage";
import ShareThis from "../../components/utils/shareThis";

const BlogDetailPage: React.FC = () => {
  const { id: currentId, categoryId: currentCategoryId } = useParams<{
    id: string;
    categoryId: string;
  }>();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [allBlogs, setAllBlogs] = useState<BlogType[]>([]);
  const [loaded, setLoaded] = useState<BlogType[]>([]);
  const [activeSlug, setActiveSlug] = useState<string>(currentId || "");
  const [isLoading, setIsLoading] = useState(true);
  const [bloginView, setBloginView] = useState<BlogType>();
  const [isDeleting, setIsDeleting] = useState(false);

  const [isManualScroll, setIsManualScroll] = useState(false);
  const manualScrollTimeout = useRef<number>();

  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<string, HTMLLIElement | null>>({});
  const initialScrollDone = useRef(false);
  const blockScrollRef = useRef(false);

  const { user, userRole } = useAuth();
  const isAdmin = userRole === "admin";
  const [isEdit, setIsEdit] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/");
    }
  }, []);

  const [scrollClass, setScrollClass] = useState("pageWithoutScroll");

  const handleScroll = () => {
    if (window.scrollY > 260) {
      //  Example:  change class after scrolling 50px
      setScrollClass("");
    } else {
      setScrollClass("pageWithoutScroll"); // Remove the class if not scrolled enough
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    // ...
  }, []);

  const handleDeleteSuccess = () => {
    window.location.reload();
  };

  const handleDelete = useCallback(async (blogId: string) => {
    setIsDeleting(true);
    try {
      await deleteBlog(blogId);
      setAllBlogs((prev) => prev.filter((blog) => blog.id !== blogId));
      setIsDeleting(false);
      handleDeleteSuccess();
    } catch (error) {
      console.error("Failed to delete blog:", error);
    }
  }, []);

  // Fetch all blogs once and pre-load around the active one
  useEffect(() => {
    (async () => {
      blockScrollRef.current = true;
      initialScrollDone.current = false;
      try {
        const fetched = await getBlogs();
        if (currentCategoryId) {
          setAllBlogs(
            fetched
              .filter((blog) => blog.blogCategoryId === +currentCategoryId)
              .sort(
                (a, b) =>
                  new Date(b.dateTimeInEasternTime ?? b.createdAt).getTime() -
                  new Date(b.dateTimeInEasternTime ?? a.createdAt).getTime()
              )
          );
        } else {
          setAllBlogs(
            fetched.sort(
              (a, b) =>
                new Date(b.dateTimeInEasternTime ?? b.createdAt).getTime() -
                new Date(b.dateTimeInEasternTime ?? a.createdAt).getTime()
            )
          );
        }

        const idx = fetched.findIndex((c) => c.slug === currentId);
        // just load the single target (or fallback to the first)
        if (idx >= 0) {
          setLoaded([fetched[idx]]);
        } else {
          setLoaded([fetched[0]]);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
        setTimeout(() => {
          blockScrollRef.current = false;
        }, 300);
      }
    })();
  }, [currentId, isEdit, currentCategoryId]);

  // Scroll into view only on initial landing
  /* useEffect(() => {
    if (!initialScrollDone.current && currentId && loaded.length) {
      const el = sectionRefs.current[currentId];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        initialScrollDone.current = true;
      }
    }
  }, [loaded, currentId]); */
  useEffect(() => {
    if (!initialScrollDone.current && currentId && loaded.length) {
      const tryScroll = () => {
        const el = sectionRefs.current[currentId];

        if (el) {
          // Check if all images in the section are loaded
          const imgs = el.querySelectorAll("img");
          let allLoaded = true;
          imgs.forEach((img) => {
            if (!img.complete) allLoaded = false;
          });
          if (allLoaded) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            initialScrollDone.current = true;
          } else {
            // Wait for images to load, then try again
            imgs.forEach((img) => {
              img.addEventListener("load", tryScroll, { once: true });
            });
            setTimeout(tryScroll, 1000); // fallback in case no images
          }
        } else {
          setTimeout(tryScroll, 200);
        }
      };
      tryScroll();
    }
  }, [loaded, currentId]);

  // Load a blog into view
  const loadSlug = useCallback(
    (slug: string | undefined, atStart = false) => {
      if (loaded.some((c) => c.slug === slug)) return;
      const ch = allBlogs.find((c) => c.slug === slug);
      if (!ch) return;
      setLoaded((prev) => (atStart ? [ch, ...prev] : [...prev, ch]));
    },
    [allBlogs, loaded]
  );

  // update page title and meta according to the active blog
  useEffect(() => {
    const idx = allBlogs.findIndex((c) => c.slug === activeSlug);
    setBloginView(allBlogs[idx]);
  }, [activeSlug]);

  // Load next on scroll to bottom
  useEffect(() => {
    const node = bottomRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && loaded.length) {
          const last = loaded[loaded.length - 1].slug;
          const idx = allBlogs.findIndex((c) => c.slug === last);
          if (idx >= 0 && idx < allBlogs.length - 1) {
            loadSlug(allBlogs[idx + 1].slug);
          }
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [loaded, allBlogs, loadSlug]);

  // Load previous on scroll to top
  useEffect(() => {
    const node = topRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && loaded.length) {
          const first = loaded[0].slug;
          const idx = allBlogs.findIndex((c) => c.slug === first);
          if (idx > 0) loadSlug(allBlogs[idx - 1].slug, true);
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [loaded, allBlogs, loadSlug]);

  // Update URL & nav on section in view without causing scroll
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        if (isManualScroll) return; // <-- short‑circuit while we’re in fly‑over mode

        entries.forEach((e) => {
          if (e.isIntersecting) {
            const slug = e.target.getAttribute("data-slug")!;
            if (slug !== activeSlug) {
              setActiveSlug(slug);
              currentCategoryId
                ? window.history.replaceState(
                    null,
                    "",
                    `/articles/${currentCategoryId}/${slug}`
                  )
                : window.history.replaceState(null, "", `/articles/${slug}`);
            }
          }
        });
      },
      { rootMargin: "-50% 0px -50% 0px" }
    );

    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [loaded, activeSlug, isManualScroll]);

  if (isLoading) return <StyledLoadingSpinner />;

  const onNavigationClick = (blog: BlogType) => {
    const slug = blog.slug;
    // 1a) Pause the observer
    setIsManualScroll(true);
    // 1b) Scroll there
    const target = sectionRefs.current[slug || ""];
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // 1c) Update URL immediately
    currentCategoryId
      ? window.history.replaceState(
          null,
          "",
          `/articles/${currentCategoryId}/${slug}`
        )
      : window.history.replaceState(null, "", `/articles/${slug}`);
    // 1d) After a short delay (long enough to finish the scroll), clear the flag
    clearTimeout(manualScrollTimeout.current);
    manualScrollTimeout.current = window.setTimeout(() => {
      setActiveSlug(slug || "");
      setIsManualScroll(false);
    }, 1000); // tune this to a bit longer than your scroll animation
  };

  const handleEditSuccess = () => {
    setIsEdit(true);
  };

  return (
    <div className="pageOuterWrapper">
      <Navbar />
      {bloginView && (
        <MetaHead
          title={`${bloginView.title} Fantasy Blog`}
          description={`${bloginView.title}.${
            bloginView.content || bloginView.excerpt
          }`}
          image={bloginView.image || undefined}
          url={`https://fantasyloser.com/articles/${bloginView.slug}`}
          type="blog"
          keywords={[
            bloginView.title.toLowerCase(),
            "fantasy blog",
            "fantasy themed blog",
            "fantasy stories",
            "fantasy realms",
            "fantasy legends",
            "fantasy league blog",
            "fantasy gaming blog",
            "fantasy player watch",
          ]}
        />
      )}
      <div className="mainContentWrap">
        <div className="bannerWrap">
          <div className="wrapper">
            <h2>Articles</h2>
            <div className="bannerSubTxt">
              Authentic original content from across the fantasy sports world -
              <span>
                {" "}
                from the unique perspective you can only get from Fantasy Loser.
              </span>
            </div>
          </div>
        </div>
        <div className="contentArea">
          <div className={`wrapper ${scrollClass}`}>
            <BlogDetailsSideNav
              activeSlug={activeSlug}
              onNavigationClick={onNavigationClick}
              currentCategoryId={currentCategoryId}
              allBlogs={allBlogs}
            />
            <div className="challengeRightContent">
              <ul className="challengeList">
                {allBlogs.length < 1 && (
                  <li>
                    <h3>No Articles</h3>
                  </li>
                )}
                {allBlogs.map((blog, index) => (
                  <li
                    key={blog.id}
                    id={blog.id}
                    data-slug={blog.slug}
                    ref={(el) => {
                      sectionRefs.current[blog.slug || 0] = el;
                    }}
                    className={index === 0 ? "scroll-mt-36" : "scroll-mt-20"}
                  >
                    {isAdmin && blog.id && (
                      <div className="actionBtnWrap">
                        <EditBlogModal
                          blogId={blog.id}
                          isAdmin={isAdmin}
                          onSaveSuccess={handleEditSuccess}
                          isEdit={isEdit}
                          setIsEdit={setIsEdit}
                        />
                        <DeleteModal
                          onConfirm={() => handleDelete(blog.id.toString())}
                          description="Are you sure you want to delete this blog? This action cannot be undone."
                          isDeleting={isDeleting}
                        />
                      </div>
                    )}
                    <h3 className="mainHeading">{blog.title}</h3>

                    {blog.image ? (
                      <div className="challengePic">
                        <img
                          src={blog.image}
                          alt={blog.title}
                          onLoad={(event) => {
                            addImageClass(event);
                          }}
                          style={{
                            maxHeight: blog.imageMaxHeight
                              ? blog.imageMaxHeight
                              : "",
                          }}
                        />
                      </div>
                    ) : null}

                    {blog.author && (
                      <div className="userBlkWrap">
                        <div className="userPic">
                          <img src={blog.thumbnail} alt="Author Image" />
                        </div>
                        <div className="userCont">
                          <div className="userHeading">{blog.author}</div>
                          <div className="updatedOn">
                            {formatToEasternTime(blog.dateTimeInEasternTime) ??
                              formatToET(blog.createdAt)}
                            {!!blog.readingTime && blog.readingTime > 0 && (
                              <>&nbsp;&bull;&nbsp;{blog.readingTime} min read</>
                            )}
                          </div>
                        </div>
                        <div className="socialBtnWrap">
                          <ShareThis
                            title={blog.title}
                            description={blog.excerpt || ""}
                            image={blog.image || ""}
                          />
                        </div>
                      </div>
                    )}
                    <Markdown remarkPlugins={[remarkGfm]}>
                      {blog.content}
                    </Markdown>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BlogDetailPage;
